'use client';
import { useEffect, useState } from 'react';

export interface CollectableCouponLayout {
  /**
   * Max columns the viewport supports. Drives the upper bound for
   * `buildGrid.cols`.
   *   <540px   → 1 (mobile, single-card layout)
   *   540–767  → 2 (narrow tablet)
   *   768–1279 → 3 (tablet/desktop)
   *   ≥1280    → 4 (wide desktop; 4×240+3×19.2 = 1017.6px fits)
   */
  pageSize: number;
  viewportHeight: number;
  viewportWidth: number;
}

/**
 * Track the viewport size for the responsive grid. SSR-safe (returns
 * sensible defaults when `window` is undefined).
 */
export const useCollectableCouponLayout = (): CollectableCouponLayout => {
  const [pageSize, setPageSize] = useState(3);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(0);

  useEffect(() => {
    const updateLayout = () => {
      if (typeof window === 'undefined') {
        setPageSize(3);
        setViewportHeight(0);
        setViewportWidth(0);
        return;
      }
      const w = window.innerWidth;
      setViewportWidth(w);
      if (w < 540) {
        // Mobile: 1 col, 1 row, single-card layout (locked).
        setPageSize(1);
        setViewportHeight(window.innerHeight);
        return;
      }
      // Tablet / desktop. Cols cap grows with width so a 4-col row
      // fits 4×240+3×19.2=1017.6px comfortably (≥1280px viewport).
      let cols: number;
      if (w >= 1280) cols = 4;
      else if (w >= 768) cols = 3;
      else cols = 2;
      setPageSize(cols);
      setViewportHeight(window.innerHeight);
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  return { pageSize, viewportHeight, viewportWidth };
};